/**
 * 全局消息提示封装
 * 基于 vue-sonner 的全局消息提示封装
 * 支持两种调用方式：
 *   message.success('标题')
 *   message.success('标题', '描述')
 *   message.success({ title: '标题', description: '描述', duration: 4000 })
 */
import { markRaw } from 'vue'
import { toast } from 'vue-sonner'
import CustomToast from '@/components/shared/Toast/CustomToast.vue'
import { takeRecentApiErrorMessage } from '@/utils/apiErrorDisplayState'

export interface ToastOptions {
  title: string
  description?: string
  duration?: number
}

type ToastType = 'success' | 'error' | 'warning' | 'info'
const DUPLICATE_TOAST_WINDOW_MS = 800
const MIN_RELATED_ERROR_TITLE_LENGTH = 4
const recentToasts = new Map<
  string,
  {
    id: string | number
    shownAt: number
    type: ToastType
    title: string
    description?: string
  }
>()

function resolveArgs(contentOrOptions: string | ToastOptions, descriptionOrDuration?: string | number): ToastOptions {
  if (typeof contentOrOptions === 'object') {
    return contentOrOptions
  }
  if (typeof descriptionOrDuration === 'string') {
    return { title: contentOrOptions, description: descriptionOrDuration }
  }
  return { title: contentOrOptions, duration: descriptionOrDuration }
}

function createToast(type: ToastType, options: ToastOptions) {
  if (
    type === 'error' &&
    !options.description &&
    isGenericOperationErrorTitle(normalizeToastText(options.title))
  ) {
    const apiErrorMessage = takeRecentApiErrorMessage()
    if (apiErrorMessage) {
      options = { ...options, title: apiErrorMessage }
    }
  }

  const toastKey = JSON.stringify([type, options.title, options.description ?? ''])
  const now = Date.now()
  const recentToast = recentToasts.get(toastKey)
  if (recentToast && now - recentToast.shownAt < DUPLICATE_TOAST_WINDOW_MS) {
    return recentToast.id
  }

  const relatedToast = findRelatedErrorToast(type, options, now)
  if (relatedToast) {
    if (relatedToast.action === 'suppress') {
      return relatedToast.id
    }
    recentToasts.delete(relatedToast.key)
  }

  const duration = options.duration ?? 4000
  const toastId: string | number = toast.custom(markRaw(CustomToast), {
    id: relatedToast?.action === 'replace' ? relatedToast.id : undefined,
    componentProps: {
      type,
      title: options.title,
      description: options.description,
      duration,
      onClose: () => {
        toast.dismiss(toastId)
        if (recentToasts.get(toastKey)?.id === toastId) {
          recentToasts.delete(toastKey)
        }
      }
    },
    duration: duration + 300,
    unstyled: true
  })
  recentToasts.set(toastKey, {
    id: toastId,
    shownAt: now,
    type,
    title: options.title,
    description: options.description
  })
  return toastId
}

function findRelatedErrorToast(type: ToastType, options: ToastOptions, now: number) {
  if (type !== 'error' || options.description) {
    return null
  }

  const currentTitle = normalizeToastText(options.title)
  if (currentTitle.length < MIN_RELATED_ERROR_TITLE_LENGTH) {
    return null
  }

  for (const [key, recentToast] of recentToasts.entries()) {
    if (
      recentToast.type !== 'error' ||
      recentToast.description ||
      now - recentToast.shownAt >= DUPLICATE_TOAST_WINDOW_MS
    ) {
      continue
    }

    const recentTitle = normalizeToastText(recentToast.title)
    const currentGeneric = isGenericOperationErrorTitle(currentTitle)
    const recentGeneric = isGenericOperationErrorTitle(recentTitle)
    if (currentGeneric && !recentGeneric) {
      return { action: 'suppress' as const, key, id: recentToast.id }
    }
    if (!currentGeneric && recentGeneric) {
      return { action: 'replace' as const, key, id: recentToast.id }
    }

    if (!isRelatedErrorTitle(currentTitle, recentTitle)) {
      continue
    }

    if (currentTitle.length > recentTitle.length) {
      return { action: 'replace' as const, key, id: recentToast.id }
    }
    return { action: 'suppress' as const, key, id: recentToast.id }
  }

  return null
}

function isRelatedErrorTitle(currentTitle: string, recentTitle: string): boolean {
  if (currentTitle === recentTitle) {
    return true
  }

  const shorterTitle = currentTitle.length < recentTitle.length ? currentTitle : recentTitle
  const longerTitle = currentTitle.length < recentTitle.length ? recentTitle : currentTitle
  if (shorterTitle.length < MIN_RELATED_ERROR_TITLE_LENGTH) {
    return false
  }

  return longerTitle.includes(shorterTitle)
}

function normalizeToastText(text: string): string {
  return text.trim()
}

function isGenericOperationErrorTitle(title: string): boolean {
  if (!title) return false
  if (title.includes('：') || title.includes(':')) return false
  return /(失败|错误|异常)$/.test(title)
}

export const message = {
  success(contentOrOptions: string | ToastOptions, descriptionOrDuration?: string | number) {
    const options = resolveArgs(contentOrOptions, descriptionOrDuration)
    return createToast('success', options)
  },
  error(contentOrOptions: string | ToastOptions, descriptionOrDuration?: string | number) {
    const options = resolveArgs(contentOrOptions, descriptionOrDuration)
    return createToast('error', options)
  },
  warning(contentOrOptions: string | ToastOptions, descriptionOrDuration?: string | number) {
    const options = resolveArgs(contentOrOptions, descriptionOrDuration)
    return createToast('warning', options)
  },
  info(contentOrOptions: string | ToastOptions, descriptionOrDuration?: string | number) {
    const options = resolveArgs(contentOrOptions, descriptionOrDuration)
    return createToast('info', options)
  },
  // loading 需要返回可持续更新/关闭的 sonner id，保留原生生命周期通道。
  loading(content: string, duration?: number) {
    return toast.loading(content, { duration })
  }
}
